import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';

const AdminTestimonials = () => {
  const [activeTab, setActiveTab] = useState('testimonials');
  
  // Testimonials State
  const [testimonials, setTestimonials] = useState([]);
  const [testLoading, setTestLoading] = useState(true);
  const [testUploading, setTestUploading] = useState(false);
  const testFileRef = useRef(null);
  const [testData, setTestData] = useState({ name: '', role: '', location: '', quote: '', file: null });

  // Trusted Clients State
  const [clients, setClients] = useState([]);
  const [clientLoading, setClientLoading] = useState(true);
  const [clientUploading, setClientUploading] = useState(false);
  const clientFileRef = useRef(null);
  const [clientData, setClientData] = useState({ name: '', color_grading: 'none', file: null });

  useEffect(() => {
    fetchTestimonials();
    fetchClients();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const { data, error } = await supabase.from('testimonials').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setTestimonials(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTestLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase.from('trusted_clients').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setClientLoading(false);
    }
  };

  const handleTestUpload = async (e) => {
    e.preventDefault();
    if (!testData.file) return alert('Select an avatar image.');
    setTestUploading(true);
    try {
      const fileExt = testData.file.name.split('.').pop();
      const filePath = `avatars/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('hbb_files').upload(filePath, testData.file);
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage.from('hbb_files').getPublicUrl(filePath);
      const imageUrl = publicUrlData.publicUrl;

      const { error: dbError } = await supabase.from('testimonials').insert([{
        name: testData.name, role: testData.role, location: testData.location, quote: testData.quote, image_url: imageUrl
      }]);
      if (dbError) throw dbError;

      alert('Testimonial added!');
      setTestData({ name: '', role: '', location: '', quote: '', file: null });
      if (testFileRef.current) testFileRef.current.value = '';
      fetchTestimonials();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setTestUploading(false);
    }
  };

  const handleClientUpload = async (e) => {
    e.preventDefault();
    if (!clientData.file) return alert('Select a logo image.');
    setClientUploading(true);
    try {
      const fileExt = clientData.file.name.split('.').pop();
      const filePath = `logos/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('hbb_files').upload(filePath, clientData.file);
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage.from('hbb_files').getPublicUrl(filePath);
      const logoUrl = publicUrlData.publicUrl;

      const { error: dbError } = await supabase.from('trusted_clients').insert([{
        name: clientData.name, color_grading: clientData.color_grading, logo_url: logoUrl
      }]);
      if (dbError) throw dbError;

      alert('Trusted client added!');
      setClientData({ name: '', color_grading: 'none', file: null });
      if (clientFileRef.current) clientFileRef.current.value = '';
      fetchClients();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setClientUploading(false);
    }
  };

  const deleteTestimonial = async (id, url) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      const pathParts = url.split('hbb_files/');
      if (pathParts.length === 2) await supabase.storage.from('hbb_files').remove([pathParts[1]]);
      await supabase.from('testimonials').delete().eq('id', id);
      fetchTestimonials();
    } catch (err) { alert('Delete failed'); }
  };

  const deleteClient = async (id, url) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      const pathParts = url.split('hbb_files/');
      if (pathParts.length === 2) await supabase.storage.from('hbb_files').remove([pathParts[1]]);
      await supabase.from('trusted_clients').delete().eq('id', id);
      fetchClients();
    } catch (err) { alert('Delete failed'); }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white flex flex-col gap-8">
      <h2 className="text-3xl font-bold text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Testimonials & Clients</h2>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-800 pb-2 overflow-x-auto whitespace-nowrap">
        <button 
          onClick={() => setActiveTab('testimonials')}
          className={`px-6 py-2 rounded-t font-bold transition-colors shrink-0 ${activeTab === 'testimonials' ? 'bg-[#1c1b1b] text-[#f59e0b] border-t border-l border-r border-gray-800' : 'text-gray-500 hover:text-white'}`}
        >
          What Our Clients Say
        </button>
        <button 
          onClick={() => setActiveTab('clients')}
          className={`px-6 py-2 rounded-t font-bold transition-colors shrink-0 ${activeTab === 'clients' ? 'bg-[#1c1b1b] text-[#f59e0b] border-t border-l border-r border-gray-800' : 'text-gray-500 hover:text-white'}`}
        >
          Trusted Clients
        </button>
      </div>

      {activeTab === 'testimonials' && (
        <div className="space-y-8">
          <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 shadow-xl max-w-4xl">
            <h3 className="text-xl font-semibold mb-6 text-[#d8c3ad]">Add Testimonial</h3>
            <form onSubmit={handleTestUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input type="text" placeholder="Client Name" value={testData.name} onChange={e => setTestData({...testData, name: e.target.value})} required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <input type="text" placeholder="Role (e.g. CEO, Homeowner)" value={testData.role} onChange={e => setTestData({...testData, role: e.target.value})} required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <input type="text" placeholder="Location" value={testData.location} onChange={e => setTestData({...testData, location: e.target.value})} required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <input type="file" accept="image/*" ref={testFileRef} onChange={e => setTestData({...testData, file: e.target.files[0]})} required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <textarea placeholder="Quote" rows="3" value={testData.quote} onChange={e => setTestData({...testData, quote: e.target.value})} required className="md:col-span-2 bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none resize-none"></textarea>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={testUploading} className={`font-bold py-3 px-8 rounded transition-colors flex items-center gap-2 ${testUploading ? 'bg-gray-600 text-gray-400' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}>
                  {testUploading ? 'Processing...' : 'Add Testimonial'}
                </button>
              </div>
            </form>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl">
            {testimonials.map(t => (
              <div key={t.id} className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 relative">
                <button onClick={() => deleteTestimonial(t.id, t.image_url)} className="absolute top-4 right-4 text-red-500 hover:text-red-400"><span className="material-symbols-outlined">delete</span></button>
                <div className="flex gap-4 items-center mb-4">
                  <img src={t.image_url} alt={t.name} className="w-16 h-16 rounded-full object-cover border border-[#f59e0b]" />
                  <div>
                    <h4 className="font-bold text-lg">{t.name}</h4>
                    <p className="text-sm text-gray-400">{t.role} • {t.location}</p>
                  </div>
                </div>
                <p className="text-gray-300 italic text-sm">"{t.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'clients' && (
        <div className="space-y-8">
          <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 shadow-xl max-w-4xl">
            <h3 className="text-xl font-semibold mb-6 text-[#d8c3ad]">Add Trusted Client Logo</h3>
            <form onSubmit={handleClientUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input type="text" placeholder="Company Name" value={clientData.name} onChange={e => setClientData({...clientData, name: e.target.value})} required className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <select value={clientData.color_grading} onChange={e => setClientData({...clientData, color_grading: e.target.value})} className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none">
                <option value="none">Original Colors (None)</option>
                <option value="grayscale">Grayscale</option>
                <option value="invert">Invert (White Logos)</option>
                <option value="sepia">Sepia (Gold tint)</option>
              </select>
              <input type="file" accept="image/*" ref={clientFileRef} onChange={e => setClientData({...clientData, file: e.target.files[0]})} required className="md:col-span-2 bg-[#0e0e0e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none" />
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={clientUploading} className={`font-bold py-3 px-8 rounded transition-colors flex items-center gap-2 ${clientUploading ? 'bg-gray-600 text-gray-400' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}>
                  {clientUploading ? 'Processing...' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-7xl">
            {clients.map(c => (
              <div key={c.id} className="bg-[#1c1b1b] p-4 rounded-xl border border-white/10 flex flex-col items-center relative group">
                <button onClick={() => deleteClient(c.id, c.logo_url)} className="absolute top-2 right-2 text-red-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><span className="material-symbols-outlined text-sm">delete</span></button>
                <div className="w-full h-24 flex items-center justify-center p-2 mb-2 bg-[#0e0e0e] rounded">
                  <img src={c.logo_url} alt={c.name} className="max-h-full max-w-full object-contain" style={{ filter: c.color_grading !== 'none' ? `${c.color_grading}(100%)` : 'none' }} />
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-widest text-center truncate w-full">{c.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
